<script setup lang="ts">
import { KnowledgeItem } from '~/types/types'
import LayoutSection from '@/components/layout/LayoutSection.vue'
import CardGrid from '@/components/layout/CardGrid.vue'
import AppHeading from '@/components/app/AppHeading.vue'
import AppChips from '~/components/app/AppChips.vue'
import AppChipsItem from '~/components/app/AppChipsItem.vue'
import AppLink from '~/components/app/AppLink.vue'
const { slug } = useRoute().params
const { find } = useStrapi()
const knowledgeItem: Ref<KnowledgeItem | undefined> = ref()
const relatedPosts: Ref<KnowledgeItem[] | undefined> = ref()

await find<KnowledgeItem>('knowledgebases', {
  populate: '*',
  filters: {
    slug: {
      $eq: slug,
    },
  },
})
  .then(async (response) => {
    knowledgeItem.value = response.data[0].attributes
    await find<KnowledgeItem>('knowledgebases', {
      populate: '*',
      filters: {
        topic: {
          name: knowledgeItem.value.topic.data.attributes.name,
        },
        title: {
          $not: knowledgeItem.value.title,
        },
      },
    }).then((response) => {
      relatedPosts.value = response.data.map((item) => item.attributes)
    })
  })
  .catch((error) => {
    console.error(error)
  })

useSeoMeta({
  title: () =>
    `Drivmedla - ${knowledgeItem.value ? `${knowledgeItem.value.title}` : 'Kunskapsbank'}`,
  description: () =>
    `${
      knowledgeItem.value?.description
        ? knowledgeItem.value.description
        : 'Lär dig mer om omställningen till fossilfria alternativ'
    }`,
  ogTitle: () =>
    `Drivmedla - ${knowledgeItem.value ? `${knowledgeItem.value.title}` : 'Kunskapsbank'}`,
  ogDescription: () =>
    `${
      knowledgeItem.value?.description
        ? knowledgeItem.value.description
        : 'Lär dig mer om omställningen till fossilfria alternativ'
    }`,
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})

const getKnowledgeBaseTags = (knowledgebase: KnowledgeItem) => {
  let allTags = knowledgebase.tags?.data.map((tag) => tag.attributes.name) || []
  allTags = [...allTags]
  if (knowledgebase.topic.data?.attributes.name) {
    allTags.push(knowledgebase.topic.data?.attributes.name)
  }
  if (knowledgebase.subtopic.data?.attributes.name) {
    allTags.push(knowledgebase.subtopic.data?.attributes.name)
  }
  return allTags
}
</script>
<template>
  <main>
    <LayoutSection v-if="knowledgeItem" size="small">
      <AppLink
        class="link-back"
        title="Utforska fler inlägg inom kunskapsbanken"
        path="/kunskapsbank"
        icon="ArrowLeftIcon"
      />
      <AppHeading :title="knowledgeItem.title" type="h1" size="large" />
      <AppChips v-if="knowledgeItem.tags" size="large">
        <AppChipsItem
          v-if="knowledgeItem.topic.data"
          :item="knowledgeItem.topic.data.attributes.name"
          type="clear"
        />
        <AppChipsItem
          v-if="knowledgeItem.subtopic.data"
          :item="knowledgeItem.subtopic.data.attributes.name"
          type="clear"
        />
        <AppChipsItem
          v-for="tag in knowledgeItem.tags.data"
          :key="tag.id"
          :item="tag.attributes.name"
          type="selected"
        />
      </AppChips>
      <p class="pt-3">{{ knowledgeItem.description }}</p>
      <NuxtLink
        v-if="knowledgeItem.link"
        :href="`${knowledgeItem.link}`"
        target="_blank"
        class="ml-auto"
      >
        <Button
          class="green-button outlined"
          label="Gå till extern länk"
          icon="pi pi-external-link"
          icon-pos="right"
          severity="secondary"
          outlined
          rounded
        />
      </NuxtLink>
      <div v-if="relatedPosts?.length" class="my-8">
        <AppHeading title="Relaterade inlägg" type="h2" size="large" />
        <CardGrid :grid="2">
          <CardKnowledgeBase
            v-for="(knowledgebase, index) in relatedPosts"
            :key="index"
            :title="knowledgebase.title"
            :description="knowledgebase.description"
            :slug="knowledgebase.slug"
            :tags="getKnowledgeBaseTags(knowledgebase)"
          />
        </CardGrid>
      </div>
    </LayoutSection>
  </main>
</template>
